import { apiService } from './apiService';
import type {
  StatisticsPeriod,
  StatisticsInterviewStatusEnum
} from '../../generated-src/client/statistics/api';
import { 
  StatisticsPeriod as StatisticsPeriodEnum
} from '../../generated-src/client/statistics/api';

// Импортируем и реэкспортируем сгенерированные типы
export type { OverviewStats } from '../../generated-src/client/statistics/api';
export type { ActivityByDay } from '../../generated-src/client/statistics/api';
export type { RecruiterDetailItem } from '../../generated-src/client/statistics/api';
export type { InterviewDetailItem } from '../../generated-src/client/statistics/api';

export interface StatisticsFilters {
  period?: StatisticsPeriod;
  status?: StatisticsInterviewStatusEnum;
  search?: string;
  positionId?: number;
  page?: number;
  size?: number;
}

/**
 * Сервис для работы с API статистики
 * Интегрирует данные из OpenAPI спецификации в компонент StatisticsPage
 */
class StatisticsService {
  /**
   * Получить общую статистику для карточек и распределения по статусам
   */
  async getOverviewStats(
    period: StatisticsPeriod = StatisticsPeriodEnum.ALL,
    positionId?: number
  ): Promise<import('../../generated-src/client/statistics/api').OverviewStats> {
    try {
      console.log('🔗 StatisticsService: Вызываем API getOverviewStats', { period, positionId });
      const client = apiService.getAuthenticatedClient();
      // новый контракт: (period, positionId?)
      const response = await client.statistics.getOverviewStats(period, positionId);
      console.log('🔗 StatisticsService: Получен ответ от API getOverviewStats:', response.data);
      return response.data;
    } catch (error) {
      console.error('Ошибка загрузки общей статистики:', error);
      throw new Error('Не удалось загрузить общую статистику');
    }
  }

  /**
   * Получить данные для графика активности
   */
  async getActivityStats(
    period: StatisticsPeriod,
    positionId?: number
  ): Promise<import('../../generated-src/client/statistics/api').ActivityByDay[]> {
    try {
      const client = apiService.getAuthenticatedClient();
      const response = await client.statistics.getActivityStats(period, positionId);
      return response.data;
    } catch (error) {
      console.error('Ошибка загрузки данных активности:', error);
      throw new Error('Не удалось загрузить данные активности');
    }
  }

  /**
   * Получить эффективность рекрутеров
   */
  async getRecruiterStats(
    period: StatisticsPeriod = StatisticsPeriodEnum.ALL,
    positionId?: number
  ): Promise<import('../../generated-src/client/statistics/api').RecruiterDetailItem[]> {
    try {
      const client = apiService.getAuthenticatedClient();
      const response = await client.statistics.getRecruiterStats(period, positionId);
      return response.data;
    } catch (error) {
      console.error('Ошибка загрузки статистики рекрутеров:', error);
      throw new Error('Не удалось загрузить статистику рекрутеров');
    }
  }

  /**
   * Получить детальную таблицу собеседований
   */
  async getInterviewDetails(filters: StatisticsFilters = {}): Promise<import('../../generated-src/client/statistics/api').InterviewDetailItem[]> {
    try {
      console.log('🔗 StatisticsService: Вызываем API getInterviewDetails с фильтрами:', filters);
      const client = apiService.getAuthenticatedClient();
      const response = await client.statistics.getInterviewDetails(
        filters.period || StatisticsPeriodEnum.ALL,
        filters.status,
        filters.search,
        filters.positionId,
        filters.page || 0,
        filters.size || 20
      );
      console.log('🔗 StatisticsService: Получен ответ от API getInterviewDetails:', response.data?.length || 0, 'записей');
      return response.data;
    } catch (error) {
      console.error('Ошибка загрузки деталей собеседований:', error);
      throw new Error('Не удалось загрузить детали собеседований');
    }
  }

  /**
   * Получить «Итоги Подбора» — только одобренные кандидаты (TASKNEIROKLYUCH-792).
   * Бекенд сам мерджит AI-интервью и live-слоты из ai-recruiter, применяет единое правило фильтрации.
   */
  async getRecruitmentResults(filters: StatisticsFilters = {}): Promise<import('../../generated-src/client/statistics/api').InterviewDetailItem[]> {
    try {
      const client = apiService.getAuthenticatedClient();
      const response = await client.statistics.getRecruitmentResults(
        filters.period || StatisticsPeriodEnum.ALL,
        filters.search,
        filters.positionId,
        filters.page || 0,
        filters.size || 100
      );
      return response.data;
    } catch (error) {
      console.error('Ошибка загрузки Итогов Подбора:', error);
      throw new Error('Не удалось загрузить Итоги Подбора');
    }
  }

  /**
   * Загрузить все данные для страницы статистики
   */
  async getAllStatisticsData(
    period: StatisticsPeriod = StatisticsPeriodEnum.ALL,
    positionId?: number
  ) {
    try {
      const [overviewStats, recruiterStats] = await Promise.all([
        this.getOverviewStats(period, positionId),
        this.getRecruiterStats(period, positionId)
      ]);

      return {
        overviewStats,
        recruiterStats
      };
    } catch (error) {
      console.error('Ошибка загрузки всех данных статистики:', error);
      throw error;
    }
  }

  /**
   * Загрузить данные активности для графика
   */
  async getActivityDataForChart(period: StatisticsPeriod, positionId?: number) {
    try {
      const activityData = await this.getActivityStats(period, positionId);
      
      // Преобразуем данные в формат для графика
      return activityData.map(item => ({
        date: this.formatDateForChart(item.date),
        candidates: item.candidates,
        interviews: item.interviews,
        applications: item.applications
      }));
    } catch (error) {
      console.error('Ошибка загрузки данных для графика:', error);
      throw error;
    }
  }

  /**
   * Форматировать дату для отображения в графике (DD.MM)
   */
  private formatDateForChart(dateString: string): string {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${day}.${month}`;
    } catch (error) {
      console.error('Ошибка форматирования даты:', error);
      return dateString;
    }
  }

  /**
   * Форматировать дату для отображения в таблице (DD.MM.YYYY)
   */
  formatDateForTable(dateString: string): string {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      console.error('Ошибка форматирования даты:', error);
      return dateString;
    }
  }
}

// Экспортируем единственный экземпляр сервиса
export const statisticsService = new StatisticsService();
